import { SearchInput } from "components/Search/SearchInput"
import { useBoolean, useNumber, useString, useWindowSize } from "helpers/hooks"
import { useCallback, useEffect, useState } from "react"
import {
  AssignmentGroupQueryState,
  AssignmentGroupResponse,
} from "../user.type"
import SimpleTable, { SimpleTableProps } from "components/Table/SimpleTable"
import { cloneDeep, debounce } from "lodash"
import { Pagination } from "components/Pagination/Pagination"
import { getAssignmentGroupByUserIdMiddleware } from "../api.service"
import { TippyCustomzie, useTippyLayout } from "components/TippyCustomzie"
import { ClearAll, FilterIcon } from "components/Filter"
import FormSelectFilter from "pages/conversations/molecules/FormSelectFilter"
import { FilterHeader } from "components/Filter/FilterHeader"
import { FilterCollapse } from "components/Filter/FilterCollapse"
import { GroupsOrganizationRoleResponse } from "pages/groups/groups.type"
import {
  getGroupsOrganizationRolesMiddleware,
  postUnassignMemberFromGroupsOrganizationMiddleware,
} from "pages/groups/groups.api"
import Button from "components/Button/Button"
import { formatDate } from "helpers/utils"
import { ReactComponent as IconCloseBlack } from "assets/images/icons/icon-close-black.svg"
import Tippy from "@tippyjs/react"
import { configureStore } from "stores/configureStore"
import { closeModal, openModal } from "reducers/modal"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { toast } from "react-toastify"

interface GroupTabProps {
  userId: string
  isAdmin: boolean
}

export const GroupTab = (props: GroupTabProps) => {
  const { userId, isAdmin } = props
  const { isMobile } = useWindowSize()
  const search = useString()
  const [groups, setGroups] = useState<AssignmentGroupResponse[]>([])
  const totalPage = useNumber(0)
  const totalLabel = useString("")
  const [queryState, setQueryState] = useState<AssignmentGroupQueryState>({
    page: 1,
    pageSize: 25,
  })
  const showFilter = useBoolean(false)
  const [roles, setRoles] = useState<GroupsOrganizationRoleResponse[]>([])
  const [filterRole, setFilterRole] = useState<{ [key: string]: string }>({
    "-1": "-1",
  })
  const { TippyLayout } = useTippyLayout()
  useEffect(() => {
    getGroups(queryState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryState.pageSize,
    queryState.page,
    queryState.search,
    queryState.sort,
    queryState.order,
    queryState.roles,
  ])

  useEffect(() => {
    getAllRole()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const getAllRole = async () => {
    const response = await getGroupsOrganizationRolesMiddleware()
    setRoles(response.data)
  }

  const getGroups = async (newQueyState: AssignmentGroupQueryState) => {
    const response = await getAssignmentGroupByUserIdMiddleware(
      newQueyState,
      userId || ""
    )
    setGroups(response.data)
    totalPage.setValue(response.total)
    totalLabel.setValue(response.totalLabel)
  }

  const debounceSearch = useCallback(
    debounce((newSearch: string, newQueyState: AssignmentGroupQueryState) => {
      setQueryState({
        ...newQueyState,
        search: newSearch.trim(),
        page: 1,
      })
    }, 500),
    []
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      search.setValue(value)
      debounceSearch(value, cloneDeep(queryState))
    },
    [debounceSearch, queryState, search]
  )

  const handleSearchClear = useCallback(() => {
    search.setValue("")
    setQueryState({
      ...queryState,
      search: "",
    })
  }, [queryState, search])

  const handleChangePage = (newPage: number, newLimit: number) => {
    setQueryState({
      ...queryState,
      page: newPage,
      pageSize: newLimit,
    })
  }

  const onCheckActiveFilter = () => {
    return Object.keys(filterRole).length > 0 && !filterRole["-1"]
  }

  const onChangeRoleFilter = (chosenValue: any) => {
    const newChosenValue = cloneDeep(chosenValue)
    setFilterRole((prev) => {
      if (
        Object.keys(newChosenValue).length === 0 ||
        (prev["-1"] !== "-1" && newChosenValue["-1"] === "-1")
      ) {
        return { "-1": "-1" }
      }
      if (
        newChosenValue["-1"] === "-1" &&
        Object.keys(newChosenValue).length > 1
      ) {
        delete newChosenValue["-1"]
      }

      return newChosenValue
    })
  }

  const onClearRoleFilter = () => {
    const roleFilter =
      queryState.roles && queryState.roles !== ""
        ? JSON.parse(queryState.roles).reduce(
          (acc, role) => ({ ...acc, [role]: role }),
          {}
        )
        : { "-1": "-1" }

    setFilterRole(roleFilter)
    showFilter.setValue(false)
  }

  const onApplyFilter = () => {
    showFilter.setValue(false)
    setQueryState({
      ...queryState,
      page: 1,
      roles: (() => {
        const filteredRoles = Object.keys(filterRole)
          .filter((key) => key !== "-1")
          .map((key) => filterRole[key])
        return filteredRoles.length === 0 ? "" : JSON.stringify(filteredRoles)
      })(),
    })
  }

  const renderFilter = () => {
    const rolerFilters = [
      {
        key: "All",
        value: "-1",
      },
      ...roles.map((role) => ({
        key: role.name,
        value: role.id,
      })),
    ]

    if (isMobile) {
      return (
        <TippyLayout
          visible={showFilter.value}
          headerContent={
            <FilterHeader
              setVisible={showFilter.setValue}
              onCancel={onClearRoleFilter}
              onApply={onApplyFilter}
            />
          }
          mainContent={
            <div>
              <ClearAll
                onClick={() => {
                  setFilterRole({ "-1": "-1" })
                }}
                active={onCheckActiveFilter()}
                containerClass="border-b border-[#E4E4E4]"
              />

              <div className="flex flex-col">
                <FilterCollapse
                  label="Role"
                  multiActive
                  data={rolerFilters.map((el) => ({
                    value: el.value,
                    label: el.key,
                  }))}
                  chosenValue={filterRole}
                  onChange={onChangeRoleFilter}
                />
              </div>
            </div>
          }
        >
          <FilterIcon
            onClick={() => {
              showFilter.setValue(!showFilter.value)
            }}
            activeIcon={queryState.roles ? true : false}
          />
        </TippyLayout>
      )
    }
    return (
      <TippyCustomzie
        containerClass="component-card-menu"
        placement="bottom-end"
        interactive
        arrow={false}
        animation="scale"
        offset={[280, 20]}
        allowHTML
        visible={showFilter.value}
        content={
          <div
            className="pt-3.5 pb-3"
            style={{
              background: "#222222",
              borderRadius: 4,
              width: 250,
            }}
          >
            <div className="flex items-center justify-between mb-3 px-3 select-none">
              <p className="hidden md:block font-semibold text-16 leading-26 text-white">
                Filter
              </p>
              <ClearAll
                onClick={() => {
                  setFilterRole({ "-1": "-1" })
                }}
                active={onCheckActiveFilter()}
              />
            </div>

            <div className="flex">
              <FormSelectFilter
                title="Role"
                type="multi"
                dataSelects={rolerFilters}
                styleKey={{
                  maxWidth: "100%",
                }}
                styleRoot={{
                  width: "100%",
                }}
                objActiveSelect={filterRole}
                setObjActiveSelect={onChangeRoleFilter}
              />
            </div>

            <div className="flex justify-end mt-3 mr-3">
              <Button
                title="Apply"
                onClick={onApplyFilter}
                widthBtn={120}
                heightBtn={42}
              />
            </div>
          </div>
        }
        onClickOutside={onClearRoleFilter}
      >
        <FilterIcon
          onClick={() => {
            showFilter.setValue(!showFilter.value)
          }}
          activeIcon={queryState.roles ? true : false}
        />
      </TippyCustomzie>
    )
  }
  const handleSort = (
    dataIndex: string,
    sortDirection: "asc" | "desc" | null
  ) => {
    setQueryState({
      ...queryState,
      sort: sortDirection ? dataIndex : "",
      order: sortDirection || "",
      page: 1, // Reset to first page when sorting
    })
  }
  const handleUnassignGroup = (groupId: string) => {
    const handleSubmit = async () => {
      try {
        await postUnassignMemberFromGroupsOrganizationMiddleware({
          user_id: userId || "",
          group_organization_id: groupId,
        })
        if (queryState.page === 1) {
          getGroups(queryState)
        } else {
          setQueryState({
            ...queryState,
            page: 1,
          })
        }
        toast(
          <LabelNotificationPage
            messenger="Unassign successfully!"
            type="success"
          />
        )
        closeModal()
      } catch (error) {
        closeModal()
        toast(
          <LabelNotificationPage messenger="Unassign failed!" type="error" />
        )
      }
    }
    configureStore.dispatch(
      openModal({
        type: "Delete",
        props: {
          deleteModal: {
            title:
              "Unassigning this member from the group will revoke their access to all projects associated with the group.",
            label: "Unassign",
            content: "This action cannot be undone!",
            styleTitle: { textAlign: "center" },
            titleButton: "Unassign",
            colorYellowButton: false,
            onSubmit: handleSubmit,
          },
        },
      })
    )
  }
  const columnsMobile: SimpleTableProps["columns"] = [
    {
      title: "Group",
      dataIndex: "name",
      render: (columnData: AssignmentGroupResponse) => {
        return (
          <div className="flex flex-col gap-[8px]">
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Group:{" "}
              <span className="font-semibold">{`${columnData.code} - ${columnData.name}`}</span>
            </p>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Role:{" "}
              <span className="font-semibold">{columnData.role_name}</span>
            </p>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Created:{" "}
              <span className="">
                {columnData.created_at
                  ? formatDate(columnData.created_at, "DD MMM yyyy - HH:mm A")
                  : "-"}
              </span>
            </p>
          </div>
        )
      },
    },
  ]

  const columns: SimpleTableProps["columns"] = [
    {
      title: "Group",
      dataIndex: "name",
      render: (columnData: AssignmentGroupResponse) => (
        <p className="pl-[20px]">{`${columnData.code} - ${columnData.name}`}</p>
      ),
    },
    {
      title: "Role",
      dataIndex: "role_name",
      sortable: true,
      render: (columnData: AssignmentGroupResponse) => (
        <p
          className="font-semibold text-[13px] leading-[22px] text-[#111111]"
          style={{
            whiteSpace: "nowrap",
          }}
        >
          {columnData.role_name}
        </p>
      ),
      width: `200px`,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      sortable: true,
      render: (columnData: AssignmentGroupResponse) => (
        <p
          className=" text-[13px] leading-[22px] text-[#111111]"
          style={{
            whiteSpace: "nowrap",
          }}
        >
          {columnData.created_at
            ? formatDate(columnData.created_at, "DD MMM yyyy - HH:mm A")
            : "-"}
        </p>
      ),
      width: `200px`,
    },
    {
      title: "",
      dataIndex: "action",
      align: "right",
      width: "100px",
      render: (columnData: AssignmentGroupResponse) => {
        if (!isAdmin) {
          return null
        }
        return (
          <div className="flex items-center justify-end">
            <Tippy
              className="custom-tippy-menu-left flex-1"
              arrow={false}
              placement="bottom"
              content={
                <span
                  className="font-normal"
                  style={{
                    fontSize: 13,
                    lineHeight: "22px",
                  }}
                >
                  Unassign
                </span>
              }
            >
              <div
                onClick={() => handleUnassignGroup(columnData.group_id || columnData.id)}
                className="cursor-pointer flex items-center justify-center ml-[16px] bg-[#EA454526] rounded-[4px]"
                style={{
                  width: 26,
                  height: 26,
                }}
              >
                <IconCloseBlack className="icon-close-red" />
              </div>
            </Tippy>
          </div>
        )
      },
    },
  ]
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between bg-[#FFFFFF] border-b border-[#E4E4E4] min-h-[63px] px-[24px]">
        <h2 className="text-[16px] font-semibold lh-26 text-black">
          {`Groups ${totalLabel.value ? `(${totalLabel.value})` : ""}`}
        </h2>
        <div className="flex items-center gap-[12px]">
          {isMobile ? null : (
            <SearchInput
              value={search.value}
              onChange={handleSearchChange}
              onClear={handleSearchClear}
              placeholder="Search by group code or name"
            />
          )}
          {renderFilter()}
        </div>
      </div>
      <div className="flex flex-col py-6 pl-6 flex-1 overflow-hidden">
        <div className="flex flex-col pr-6 overflow-auto">
          <div className="text-black bg-white border border-border-gray rounded-md custom-table pb-[6px]">
            <SimpleTable
              customClass="storage-usage-table"
              columns={isMobile ? columnsMobile : columns}
              dataSource={groups || []}
              isRequiredTableHeader={isMobile ? false : true}
              onSort={handleSort}
            />
          </div>
          {totalPage.value ? (
            <Pagination
              total={totalPage.value}
              queryState={{
                page: queryState.page,
                limit: queryState.pageSize,
              }}
              handleChangePage={handleChangePage}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}
